import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { id: "who", label: "Who" },
  { id: "scale", label: "Scale" },
  { id: "content", label: "Content" },
  { id: "audience", label: "Audience" },
  { id: "network", label: "Network" },
  { id: "brands", label: "Brands" },
  { id: "why", label: "Why Us" },
  { id: "contact", label: "Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [time, setTime] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const tick = () => {
      const d = new Date();
      const hh = String(d.getUTCHours()).padStart(2, "0");
      const mm = String(d.getUTCMinutes()).padStart(2, "0");
      const ss = String(d.getUTCSeconds()).padStart(2, "0");
      setTime(`${hh}:${mm}:${ss} UTC`);
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, []);

  const scrollTo = (id) => (e) => {
    if (e) {
      e.preventDefault();
    }
    setOpen(false);
    // Defer so React can apply drawer close before we measure + scroll
    const run = () => {
      const el = document.getElementById(id);
      if (!el) return;
      const navHeight = window.innerWidth >= 768 ? 80 : 64;
      const top = el.getBoundingClientRect().top + window.scrollY - navHeight + 1;
      try {
        window.scrollTo({ top, behavior: "smooth" });
      } catch {
        window.scrollTo(0, top);
      }
    };
    // Two RAFs ensure React commit has flushed
    requestAnimationFrame(() => requestAnimationFrame(run));
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#05050A]/80 border-b border-cyan-500/15"
          : "bg-transparent"
      }`}
      data-testid="uth-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#hero"
          onClick={scrollTo("hero")}
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <span className="relative w-2.5 h-2.5 rounded-full bg-cyan-400 pulse-dot shadow-[0_0_12px_#00F0FF]" />
          <span className="font-display font-black text-lg md:text-xl tracking-tight text-white group-hover:text-cyan-300 transition-colors">
            U&apos;TH<span className="text-cyan-400">.</span>TIMES
          </span>
          <span className="hidden md:inline micro-label text-white/40 ml-2">// UTH_MEDIA_9X</span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-1" data-testid="nav-links">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              onClick={scrollTo(l.id)}
              className="px-4 py-2 text-xs font-mono-uth uppercase tracking-[0.18em] text-white/70 hover:text-cyan-300 transition-colors whitespace-nowrap cursor-pointer"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-2 micro-label text-white/50">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
            SYS.ON · {time}
          </div>
          <a
            href="#contact"
            onClick={scrollTo("contact")}
            className="hidden lg:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-400/55 text-cyan-300 hover:text-black hover:bg-cyan-300 transition-all duration-300 font-mono-uth uppercase text-[11px] tracking-[0.2em] whitespace-nowrap"
            style={{ boxShadow: "0 0 18px rgba(0,240,255,0.15)" }}
            data-testid="nav-partner-btn"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 pulse-dot" />
            Connect
          </a>
          <button
            type="button"
            className="xl:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="xl:hidden overflow-hidden border-t border-cyan-500/15 bg-[#05050A]/95 backdrop-blur-xl"
          >
            <div className="flex flex-col p-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={scrollTo(l.id)}
                  className="py-3 text-left text-sm font-mono-uth uppercase tracking-[0.18em] text-white/80 border-b border-white/5 cursor-pointer"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={scrollTo("contact")}
                className="btn-uth mt-5 justify-center"
              >
                Partner with us
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
